"use client";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { TUser } from "@/types/user";
import { useCookies } from "react-cookie";
import { URL } from "@/utils/const/url";
import Loading from "@/components/atom/Loading/Loading";
import { useRouter } from "@/navigation";
import { verifyUser } from "@/utils/auth";

interface IAuthContext {
  token: string | null;
  user: TUser | null;
}

interface IUpdateAuthContext {
  setUser: (user: TUser | null) => void;
  setToken: (token: string | null) => void;
  logout: () => void;
  retrieveUser: () => void;
}

const defaultAuthContext: IAuthContext = {
  token: null,
  user: null,
};

const AuthContext = createContext<IAuthContext>(defaultAuthContext);
const UpdateAuthContext = createContext<IUpdateAuthContext>(
  {} as IUpdateAuthContext
);

export const useUpdateAuth = () => {
  return useContext(UpdateAuthContext);
};

export const useAuth = () => {
  return useContext(AuthContext);
};

interface IProps {
  children: ReactNode;
}

const AuthProvider = ({ children }: IProps) => {
  const [cookies, setCookie, removeCookie] = useCookies(["token"]);
  const [auth, setAuth] = useState<IAuthContext>({
    token: cookies.token || null,
    user: null,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useRouter();

  const setUser = useCallback(
    (user: TUser | null) => {
      setAuth({ ...auth, user: user });
    },
    [setAuth, auth]
  );

  const setToken = (token: string | null) => {
    setAuth({ ...auth, token: token });
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    removeCookie("token");
    window.location.href = URL.LOGIN;
  };

  const retrieveUser = async () => {
    const user = await verifyUser(cookies.token);
    if (user) setUser(user);
  };

  useEffect(() => {
    if (auth.user) return;
    if (!cookies.token) return;
    if (auth.token != null) {
      (async () => {
        const user = await verifyUser(auth.token ?? "");
        if (user) setUser(user);
        //setCookie("token", auth.token, { path: "/" });
      })();
    } else {
      //removeCookie("token");
    }
  }, [auth, setUser]);

  return (
    <AuthContext.Provider value={auth}>
      <UpdateAuthContext.Provider
        value={{ setUser, setToken, logout, retrieveUser }}
      >
        {children}
      </UpdateAuthContext.Provider>
    </AuthContext.Provider>
  );
};

export default AuthProvider;
